import React, { useState, useEffect } from 'react';
import { portfolioAPI } from '../../services/api';
import './HeroSection.css';

const HeroSection: React.FC = () => {
  const [currentService, setCurrentService] = useState(0);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [projectCount, setProjectCount] = useState<number>(0);
  const [solutionCount, setSolutionCount] = useState<number>(0);
  const [loadingStats, setLoadingStats] = useState(true);

  const services = [
    {
      title: 'Web Development',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 12h20M12 2c2.5 2.5 4 6 4 10s-1.5 7.5-4 10M12 2c-2.5 2.5-4 6-4 10s1.5 7.5 4 10" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      ),
      description: 'Custom web applications with modern technologies'
    },
    {
      title: 'Mobile Apps',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" stroke="currentColor" strokeWidth="1.5"/>
          <line x1="12" y1="18" x2="12" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      description: 'Native and cross-platform mobile solutions'
    },
    {
      title: 'Cloud Services',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      description: 'Scalable cloud infrastructure and deployment'
    },
    {
      title: 'IT Consultancy',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      ),
      description: 'Expert guidance for digital transformation'
    },
    {
      title: 'Software Integration',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 12H4M12 4v16M4 4l16 16M4 20L20 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      description: 'Seamless API and system integration'
    },
    {
      title: 'Training Programs',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 6v6l4 2M12 6c-3.5 0-6 2-6 6s2.5 6 6 6 6-2 6-6-2.5-6-6-6z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M12 2v2M22 12h-2M4 12H2M12 22v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      description: 'Comprehensive tech training and workshops'
    },
    {
      title: 'Class Marks Record',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 8h8M8 12h6M8 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M4 20l5-5 3 3 8-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      description: 'Efficiently manage student examination results, calculate grades, divisions, and generate comprehensive performance reports.'
    },
  ];

  // Continuous passing text messages with SVG icons
  const passingTexts = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M12 8V12L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M8 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      text: '24/7 Technical Support Available'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      ),
      text: 'Custom Solutions for Every Business'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      ),
      text: 'Fast & Reliable Service Delivery'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M7 7H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M7 12H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M7 17H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      text: 'Secure & Scalable Infrastructure'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      text: '100% Client Satisfaction Guaranteed'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      ),
      text: 'Industry Leading Expertise'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <path d="M4 4H20V20H4V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M8 8H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M8 12H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M8 16H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M18 8L20 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M18 12L20 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      text: 'Driving Digital Transformation'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M12 8V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="12" cy="12" r="2" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      ),
      text: 'Trusted by 3+ Companies'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M12 8V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="12" cy="15" r="0.5" fill="currentColor"/>
        </svg>
      ),
      text: 'Innovative Solutions for Tomorrow'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="passing-icon">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M12 8L14 12L12 16L10 12L12 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M12 4V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M12 18V20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      text: 'Award-Winning Development Team'
    }
  ];

  // Fetch project and solution counts from API - NO FALLBACKS
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setLoadingStats(true);
        const [projectsRes, solutionsRes] = await Promise.all([
          portfolioAPI.getProjects(),
          portfolioAPI.getSolutions()
        ]);
        
        const projects = projectsRes.data || [];
        const solutions = solutionsRes.data || [];
        
        setProjectCount(projects.length);
        setSolutionCount(solutions.length);
      } catch (error) {
        console.error('Error fetching counts:', error);
        // No fallbacks - keep default 0
      } finally {
        setLoadingStats(false);
      }
    };

    fetchCounts();
  }, []);

  useEffect(() => {
    // Service rotation
    const interval = setInterval(() => {
      setCurrentService((prev) => (prev + 1) % services.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [services.length]);

  // Continuous text rotation - faster than service rotation
  useEffect(() => {
    const textInterval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % passingTexts.length);
    }, 2000); // Change text every 2 seconds
    return () => clearInterval(textInterval);
  }, [passingTexts.length]);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      const offset = 70;
      const elementPosition = servicesSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="hero-banner">
      {/* Animated Background Elements - ORIGINAL */}
      <div className="hero-animated-bg">
        <div className="hero-particle particle-1"></div>
        <div className="hero-particle particle-2"></div>
        <div className="hero-particle particle-3"></div>
        <div className="hero-particle particle-4"></div>
        <div className="hero-particle particle-5"></div>
        <div className="hero-particle particle-6"></div>
        <div className="hero-orb orb-1"></div>
        <div className="hero-orb orb-2"></div>
        <div className="hero-orb orb-3"></div>
        <div className="hero-shape shape-1"></div>
        <div className="hero-shape shape-2"></div>
        <div className="hero-shape shape-3"></div>
      </div>
      
      <div className="hero-floating-element fe-1"></div>
      <div className="hero-floating-element fe-2"></div>
      <div className="hero-floating-element fe-3"></div>
      
      <div className="hero-container">
        <div className="hero-grid">
          {/* Left Column */}
          <div className="hero-left">
            <div className="hero-tag">✦ Get Your Digital Solutions Today</div>
            <h1 className="hero-title-wrapper">
              <span className="hero-title">ZiTech Solutions</span>
            </h1>
            <p className="hero-description">
              We deliver innovative digital solutions including web and mobile app development,
              IT consultancy, software integration, and custom system development.
              <br /><br />
              Our expert team transforms your ideas into reality with modern technologies 
              and agile methodologies.
            </p>
            <div className="hero-buttons">
              <button 
                className="hero-btn hero-btn-primary"
                onClick={() => window.location.href = '/login'}
              >
                <span>Get Started</span>
                <svg className="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className="hero-btn hero-btn-secondary"
                onClick={scrollToServices}
              >
                Our Services
              </button>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">98%</span>
                <span className="stat-label">Satisfaction</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {loadingStats ? '...' : `${projectCount}+`}
                </span>
                <span className="stat-label">Projects</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {loadingStats ? '...' : `${solutionCount}+`}
                </span>
                <span className="stat-label">Solutions</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Support</span>
              </div>
            </div>
          </div>

          {/* Right Column - Service Slider */}
          <div className="hero-right">
            <div className="services-container">
              <div className="services-icon">
                {services[currentService].icon}
              </div>
              <h3 className="services-title">{services[currentService].title}</h3>
              <p className="services-description">{services[currentService].description}</p>
              <div className="services-dots">
                {services.map((_, index) => (
                  <span
                    key={index}
                    className={`dot ${index === currentService ? 'active' : ''}`}
                    onClick={() => setCurrentService(index)}
                  />
                ))}
              </div>
              <div className="service-counter">
                {String(currentService + 1).padStart(2, '0')} / {String(services.length).padStart(2, '0')}
              </div>
            </div>
            
            {/* Passing Text - Positioned at bottom like stats */}
            <div className="passing-text-container">
              <div className="passing-text-wrapper">
                <span key={currentTextIndex} className="passing-text">
                  {passingTexts[currentTextIndex].icon}
                  <span className="passing-text-content">{passingTexts[currentTextIndex].text}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;